import React, {FC, useEffect, useState} from "react";
import {
    Box, Button, CircularProgress,
    Grid, makeStyles, Typography,
} from "@material-ui/core";
import {colorInputBorder, colorWhite} from "../../../Constants/theme";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";
import {AllergiesIllnessesForm} from "../../../Components";
import moment from "moment";
import {editAnimal} from "../../../redux/pets/pets.actions";
import {useSnackbar} from "notistack";
import {
    addConditionFormTitles,
    vetConditionTypes,
} from "../../../Constants/conditionTypes";

const useStyle = makeStyles((theme) => ({
    section: {
        backgroundColor: colorWhite,
        border: "1.5px solid " + colorInputBorder,
        borderRadius: "6px",
        boxShadow: "none",
        padding: 10,
        marginRight: 20,
    },
    sectionText: {
        color: colorInputBorder,
    },
    textSecondary: {
        marginTop: "10px",
        fontSize: 18
    },
    content: {
        maxHeight: 190,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
            width: 5,
        },
        "&::-webkit-scrollbar-thumb": {
            background: colorInputBorder,
            borderRadius: "6px",
        }
    },
    issue: {
        "& + &": {
            marginTop: 10,
        }
    },
    "::-webkit-scrollbar": {
        width: 3,
    }
}));

const fields = {
    ALLERGY: "allergies",
    CHRONIC_CONDITION: "chronicConditions",
    MEDICATIONS: "medications",
    PREVENTATIVES: "medications"
};

const emptyList = {
    ALLERGY: "No known allergies",
    CHRONIC_CONDITION: "No issues recorded",
    MEDICATIONS: "No medications recorded",
    PREVENTATIVES: "No preventatives recorded"
};

const sectionTitles = {
    ALLERGY: "Allergic Reactions",
    CHRONIC_CONDITION: "Current Health Issues",
    MEDICATIONS: "Current Medications",
    PREVENTATIVES: "Current Preventatives"
};


const PetMedicalConditions: FC = () => {
    const [loaderShowed, setLoaderShowed] = useState(false);
    const [openEditForm, setOpenEditForm] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [contentType, setContentType] = useState("ALLERGY");
    const pet = useSelector((state: RootState) => state?.pets.selectedPet);
    const classes = useStyle();
    const dispatch = useAppDispatch();
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        setLoaderShowed(true);
        setTimeout(() => {
            setLoaderShowed(false);
        }, 1000);
    }, []);


    const renderList = (contentType) => {
        const fieldName = fields[contentType];
        let conditionsList = [...pet[fieldName]];
        switch (contentType) {
            case "MEDICATIONS":
                conditionsList = conditionsList.filter(({isPreventative}) => !isPreventative);
                break;
            case "PREVENTATIVES":
                conditionsList = conditionsList.filter(({isPreventative}) => isPreventative);
                break;
        }
        return (
                conditionsList?.length ?
                    conditionsList
                        .sort((a, b) => moment(b.date).diff(moment(a.date)))
                        .map(({id, name, date, isActive, ...rest}, idx) => {
                            const dateStarted = date ? moment(date).format("MM/YY") : "Date not specified";
                            const dateEnded = rest?.endDate ? moment(rest.endDate).format("MM/YY") : isActive ? "Present" : "Date not specified";
                            const notSpecified = dateStarted === "Date not specified" && dateEnded === "Date not specified";
                            return (
                                <Box className={classes.issue}>
                                    <a key={idx} style={{textDecoration: "none", cursor: "pointer", paddingBottom: "10px",}}
                                        onClick={() => {
                                            setContentType(contentType);
                                            setSelectedRecord(name);
                                            setOpenEditForm(true);
                                        }} href="#">
                                        <Typography variant="body2">{name}</Typography>
                                        <Typography
                                            variant="body2">({notSpecified ? "Date not specified" : `${dateStarted} - ${dateEnded}`})</Typography>
                                    </a>
                                </Box>
                            );
                        })
                    :
                    <Typography variant="h6" color="textSecondary">
                        {emptyList[contentType]}
                    </Typography>
        );
    };

    const submitPetCondition = async (condition) => {
        const fieldName = fields[contentType];
        let data = pet[fieldName];
        if (selectedRecord) {
            data = data.filter(item => item.name !== selectedRecord);
        }
        const fieldToEdit = {
            [fieldName]: [
                ...data,
                {...condition}
            ]
        };
        try {
            await dispatch(editAnimal(pet.id, {
                ownerId: pet.ownerId,
                ...fieldToEdit
            }));
        } catch (err) {
            enqueueSnackbar(err.errorMessage, {
                variant: "error",
                autoHideDuration: 3000
            });
        } finally {
            setSelectedRecord(null);
        }
    };

    const deletePetCondition = async (condition) => {
        const fieldName = fields[contentType];
        let data = pet[fieldName];
        if (selectedRecord) {
            data = data.filter(item => item.name !== selectedRecord);
        }
        const fieldToEdit = {
            [fieldName]: data,
        };
        try {
            await dispatch(editAnimal(pet.id, {
                ownerId: pet.ownerId,
                ...fieldToEdit
            }));
        } catch (err) {
            enqueueSnackbar(err.errorMessage, {
                variant: "error",
                autoHideDuration: 3000
            });
        } finally {
            setSelectedRecord(null);
        }
    };

    return (
        <>
            <Grid item container xs={12} spacing={2} style={{padding: "10px"}}>
                {
                    vetConditionTypes.map(({value, label}, idx) => (
                        <>
                            <Grid item xs={12} sm={6} md={7}>
                                <Box className={classes.section}>
                                    <Typography className={classes.sectionText} variant="body2">
                                        {sectionTitles[value]}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={5}>
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    fullWidth
                                    onClick={() => {
                                        setSelectedRecord(null);
                                        setContentType(value);
                                        setOpenEditForm(true);
                                    }}
                                >
                                    {addConditionFormTitles[value]}
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                {loaderShowed || !pet ?
                                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                                        <CircularProgress/>
                                    </Box>
                                    :
                                    <Box className={classes.content}>
                                        {pet && renderList(value)}
                                    </Box>
                                }
                            </Grid>
                        </>

                    ))
                }
                <AllergiesIllnessesForm
                    isOpen={openEditForm}
                    type={contentType}
                    setOpenDialog={setOpenEditForm}
                    handleSubmit={submitPetCondition}
                    itemToEdit={selectedRecord}
                    setItemToEdit={setSelectedRecord}
                    handleDelete={deletePetCondition}
                />
            </Grid>
        </>
    );
}
;

export default PetMedicalConditions;
